import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { compose } from 'redux';

import Card from '@quintoandar/cozy-core/Card';
import CardContent from '@quintoandar/cozy-core/Card/CardContent';
import Tag from '@quintoandar/cozy-core/Tag';
import Typography from '@quintoandar/cozy-core/Typography';
import Box from '@quintoandar/cozy-core/Box';
import AddAPhotoIcon from '@quintoandar/cozy-icons/AddAPhoto';

import { PHOTO_SENDER } from 'constants/photoSession';
import { buildAddressInfo } from 'utils/address';
import ROUTES from 'routes/constants';
import { photoSessionsListClickedAnalytics } from 'containers/PhotoSessions/actions';
import messages from './messages';
import { buildTags } from './buildTagAttributes';
import ProblemSessionNotice from './ProblemSessionNotice';

const PhotoSessionCard = ({
  intl,
  house,
  status,
  fixed,
  dueDate,
  photoSender,
  goToPhotoSessionDetail,
  goToReschedule,
  photoSessionId,
  hasProblem,
  rescheduleDeadline,
}) => {
  const addressInfo = buildAddressInfo(house);
  const tags = buildTags(intl, house, status, fixed, dueDate, hasProblem);
  const shouldShowOwnerPhotos = photoSender === PHOTO_SENDER.OWNER && !fixed;

  return (
    <Card noPadding onClick={hasProblem ? goToReschedule : goToPhotoSessionDetail}>
      <Box p={2}>
        <CardContent
          tags={tags}
          description={
            <React.Fragment>
              <Typography level="caption" kind="default">
                <FormattedMessage
                  {...messages.session}
                  values={{ photoSessionId }}
                />
              </Typography>

              <Box mt={1}>
                <Typography level="bodyStrong" kind="default">
                  {addressInfo}
                </Typography>
              </Box>

              {hasProblem && (
                <ProblemSessionNotice
                  rescheduleDeadline={rescheduleDeadline}
                  onReschedule={goToReschedule}
                />
              )}

              {shouldShowOwnerPhotos && (
                <Box mt={1}>
                  <Tag
                    kind="default"
                    label={intl.formatMessage(messages.ownerPhotos)}
                    icon={AddAPhotoIcon}
                  />
                </Box>
              )}
            </React.Fragment>
          }
        />
      </Box>
    </Card>
  );
};

PhotoSessionCard.propTypes = {
  intl: PropTypes.object,
  house: PropTypes.shape({
    address: PropTypes.string,
    complement: PropTypes.string,
    neighborhood: PropTypes.string,
    number: PropTypes.number,
    forSale: PropTypes.bool,
    forRent: PropTypes.bool,
  }).isRequired,
  status: PropTypes.string,
  fixed: PropTypes.bool,
  photoSender: PropTypes.oneOf(Object.values(PHOTO_SENDER)),
  dueDate: PropTypes.instanceOf(Date),
  goToPhotoSessionDetail: PropTypes.func.isRequired,
  goToReschedule: PropTypes.func.isRequired,
  photoSessionId: PropTypes.string,
  hasProblem: PropTypes.bool,
  rescheduleDeadline: PropTypes.instanceOf(Date),
};

PhotoSessionCard.defaultProps = {
  hasProblem: false,
  rescheduleDeadline: null,
};

export const mapDispatchToProps = (dispatch, { photoSessionId }) => ({
  goToPhotoSessionDetail: () => {
    dispatch(photoSessionsListClickedAnalytics(photoSessionId));
    dispatch(push(`${ROUTES.PHOTO_SESSION_DETAIL}${photoSessionId}`));
  },
  goToReschedule: () => {
    dispatch(photoSessionsListClickedAnalytics(photoSessionId));
    dispatch(push(ROUTES.RESCHEDULE_REPORTED_SESSION(photoSessionId)));
  },
});

export { PhotoSessionCard };
export default compose(
  injectIntl,
  connect(null, mapDispatchToProps),
)(PhotoSessionCard);
